import { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import { Modal } from '@/components/ui/Modal';
import { FadeInView } from '@/components/animations/FadeInView';

/**
 * 截图数据接口
 */
interface Screenshot {
  id: string;
  src: string;
  titleKey: string;
  descriptionKey: string;
}

/**
 * ScreenshotsSection 组件属性
 */
interface ScreenshotsSectionProps {
  className?: string;
}

/**
 * ScreenshotsSection 组件 - 展示产品界面截图
 * 
 * 功能：
 * - 使用 grid 布局展示截图缩略图
 * - 点击截图打开 Modal 大图预览
 * - 支持键盘方向键切换图片
 * - 所有图片使用懒加载和有意义的 alt 属性
 * 
 * 响应式布局：
 * - 桌面端（>= 1024px）: 3 列
 * - 平板端（768px - 1023px）: 2 列
 * - 移动端（< 768px）: 1 列
 */
export function ScreenshotsSection({ className = '' }: ScreenshotsSectionProps) {
  const { t } = useLanguage();
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // 定义截图数据数组（10 张截图）
  const screenshots: Screenshot[] = [
    {
      id: 'home',
      src: '/插件首页.png',
      titleKey: 'screenshots.items.0.title',
      descriptionKey: 'screenshots.items.0.description',
    },
    {
      id: 'list-extraction',
      src: '/列表提取.png',
      titleKey: 'screenshots.items.1.title',
      descriptionKey: 'screenshots.items.1.description',
    },
    {
      id: 'detail-extraction',
      src: '/详情提取.png',
      titleKey: 'screenshots.items.2.title',
      descriptionKey: 'screenshots.items.2.description',
    },
    {
      id: 'detail-concurrency',
      src: '/详情提取-并发配置.png',
      titleKey: 'screenshots.items.3.title',
      descriptionKey: 'screenshots.items.3.description',
    },
    {
      id: 'image-extraction',
      src: '/图片提取.png',
      titleKey: 'screenshots.items.4.title',
      descriptionKey: 'screenshots.items.4.description',
    },
    {
      id: 'email-extraction',
      src: '/邮箱提取.png',
      titleKey: 'screenshots.items.5.title',
      descriptionKey: 'screenshots.items.5.description',
    },
    {
      id: 'email-concurrency',
      src: '/邮箱提取-深度并发配置.png',
      titleKey: 'screenshots.items.6.title',
      descriptionKey: 'screenshots.items.6.description',
    },
    {
      id: 'results',
      src: '/结果页.png',
      titleKey: 'screenshots.items.7.title',
      descriptionKey: 'screenshots.items.7.description',
    },
    {
      id: 'results-filter',
      src: '/结果页-过滤条件.png',
      titleKey: 'screenshots.items.8.title',
      descriptionKey: 'screenshots.items.8.description',
    },
    {
      id: 'results-export',
      src: '/结果页-导出选项.png',
      titleKey: 'screenshots.items.9.title',
      descriptionKey: 'screenshots.items.9.description',
    },
  ];

  /**
   * 打开大图预览
   */
  const openModal = (index: number) => {
    setSelectedIndex(index);
    setIsModalOpen(true);
  };

  /**
   * 关闭大图预览
   */
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedIndex(null);
  };

  /**
   * 切换到上一张图片
   */
  const goToPrevious = useCallback(() => {
    if (selectedIndex !== null) {
      setSelectedIndex((selectedIndex - 1 + screenshots.length) % screenshots.length);
    }
  }, [selectedIndex, screenshots.length]);

  /**
   * 切换到下一张图片
   */
  const goToNext = useCallback(() => {
    if (selectedIndex !== null) {
      setSelectedIndex((selectedIndex + 1) % screenshots.length);
    }
  }, [selectedIndex, screenshots.length]);

  /**
   * 处理键盘方向键切换图片
   */
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isModalOpen) return;

      if (event.key === 'ArrowLeft') {
        goToPrevious();
      } else if (event.key === 'ArrowRight') {
        goToNext();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isModalOpen, selectedIndex, goToNext, goToPrevious]);

  return (
    <section
      className={`py-16 px-4 md:px-8 lg:px-16 bg-white ${className}`}
    >
      <div className="max-w-7xl mx-auto">
        {/* 区域标题 */}
        <FadeInView>
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
            {t('screenshots.title')}
          </h2>
        </FadeInView>

        {/* 截图网格 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {screenshots.map((screenshot, index) => (
            <FadeInView key={screenshot.id} delay={index * 50}>
              <div
                className="group cursor-pointer overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-all duration-200 hover:-translate-y-1"
                onClick={() => openModal(index)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    openModal(index);
                  }
                }}
                aria-label={`${t(screenshot.titleKey)} - ${t(screenshot.descriptionKey)}`}
              >
                {/* 截图缩略图 */}
                <div className="relative aspect-video bg-gray-100 overflow-hidden">
                  <img
                    src={screenshot.src}
                    alt={`${t(screenshot.titleKey)} - ${t(screenshot.descriptionKey)}`}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
                  />
                  {/* 悬浮遮罩 */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center">
                    <span className="text-white text-4xl opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      🔍
                    </span>
                  </div>
                </div>

                {/* 截图信息 */}
                <div className="p-4 bg-white">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {t(screenshot.titleKey)}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {t(screenshot.descriptionKey)}
                  </p>
                </div>
              </div>
            </FadeInView>
          ))}
        </div>
      </div>

      {/* 大图预览 Modal */}
      <Modal isOpen={isModalOpen} onClose={closeModal}>
        {selectedIndex !== null && (
          <div className="relative">
            {/* 大图 */}
            <img
              src={screenshots[selectedIndex].src}
              alt={`${t(screenshots[selectedIndex].titleKey)} - ${t(screenshots[selectedIndex].descriptionKey)}`}
              className="w-full h-auto max-h-[80vh] object-contain"
            />

            {/* 图片信息 */}
            <div className="p-6 bg-white">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {t(screenshots[selectedIndex].titleKey)}
              </h3>
              <p className="text-gray-600">
                {t(screenshots[selectedIndex].descriptionKey)}
              </p>
              <p className="text-sm text-gray-400 mt-2">
                {selectedIndex + 1} / {screenshots.length}
              </p>
            </div>

            {/* 上一张按钮 */}
            <button
              onClick={goToPrevious}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-3 shadow-lg transition-all duration-200 hover:scale-110"
              aria-label="上一张"
            >
              <svg
                className="w-6 h-6 text-gray-800"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            {/* 下一张按钮 */}
            <button
              onClick={goToNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-3 shadow-lg transition-all duration-200 hover:scale-110"
              aria-label="下一张"
            >
              <svg
                className="w-6 h-6 text-gray-800"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>

            {/* 关闭按钮 */}
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-2 shadow-lg transition-all duration-200 hover:scale-110"
              aria-label="关闭"
            >
              <svg
                className="w-6 h-6 text-gray-800"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        )}
      </Modal>
    </section>
  );
}
